const RETRY_MESSAGE = 'We could not record your acknowledgement. Please retry before checkout.';

export function createConsentController({
  document,
  fetch,
  routesRoot = '/',
  MutationObserver,
}) {
  const initialized = new WeakSet();
  let clientAccepted = null;
  let clientAcceptedVersion = null;
  let lifecycle = 'idle';
  let requestGeneration = 0;
  let writeQueue = null;

  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const resolveSurface = (container) => {
    const consent = container.querySelector('[data-trips-consent]');
    const form = consent?.form || null;
    const checkout = form
      ? all('[data-trips-checkout]').find((button) => button.form === form)
      : null;
    const versionField = form?.elements?.namedItem('attributes[Trips eSIM policy version]') || null;

    return {
      checkout,
      consent,
      status: container.querySelector('[data-trips-consent-status]'),
      version: container.dataset.tripsPolicyVersion,
      versionField,
    };
  };

  const setStatusAll = (message) => {
    all('[data-trips-consent-status]').forEach((status) => {
      status.textContent = message;
    });
  };

  const lockAll = (locked) => {
    all('[data-trips-consent]').forEach((consent) => {
      consent.disabled = locked;
    });
  };

  const setCheckoutSurface = (container, accepted) => {
    const { checkout, consent, version, versionField } = resolveSurface(container);
    const effective = Boolean(accepted && checkout && consent && version && versionField);

    if (consent) {
      consent.checked = effective;
      consent.dataset.tripsConsentCurrent = String(effective);
    }
    if (versionField) versionField.value = effective ? version : '';
    if (checkout) {
      checkout.disabled = !effective;
      checkout.setAttribute('aria-disabled', String(!effective));
    }
  };

  const setAllCheckoutSurfaces = (accepted, acceptedVersion = null) => {
    all('[data-trips-consent-root]').forEach((container) => {
      const versionMatches = container.dataset.tripsPolicyVersion === acceptedVersion;
      setCheckoutSurface(container, accepted && versionMatches);
    });
  };

  const closeAllCheckoutSurfaces = () => {
    all('[data-trips-checkout]').forEach((checkout) => {
      checkout.disabled = true;
      checkout.setAttribute('aria-disabled', 'true');
    });
    setAllCheckoutSurfaces(false);
  };

  const persistConsent = (container, accepted) => {
    const { consent, status, version } = resolveSurface(container);
    const generation = ++requestGeneration;

    clientAccepted = false;
    clientAcceptedVersion = null;
    lifecycle = 'pending';
    closeAllCheckoutSurfaces();
    lockAll(true);
    setStatusAll('');
    if (consent) consent.checked = accepted;

    if (!consent || !status || !version) {
      lifecycle = 'failed';
      closeAllCheckoutSurfaces();
      setStatusAll(RETRY_MESSAGE);
      lockAll(false);
      return Promise.resolve(false);
    }

    const run = async () => {
      try {
        const root = routesRoot.endsWith('/') ? routesRoot : `${routesRoot}/`;
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Trips eSIM digital content consent': accepted ? 'Accepted' : '',
              'Trips eSIM policy version': accepted ? version : '',
            },
          }),
        });
        if (!response.ok) throw new Error('cart_consent_write_failed');
        await response.json();

        if (generation === requestGeneration) {
          clientAccepted = accepted;
          clientAcceptedVersion = accepted ? version : null;
          lifecycle = 'idle';
          setAllCheckoutSurfaces(accepted, clientAcceptedVersion);
          setStatusAll('');
        }
        return true;
      } catch (_error) {
        if (generation === requestGeneration) {
          clientAccepted = false;
          clientAcceptedVersion = null;
          lifecycle = 'failed';
          closeAllCheckoutSurfaces();
          setStatusAll(RETRY_MESSAGE);
        }
        return false;
      } finally {
        if (generation === requestGeneration) lockAll(false);
      }
    };

    const result = writeQueue ? writeQueue.then(run, run) : run();
    writeQueue = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  };

  const initializeConsent = (root = document) => {
    all('[data-trips-consent-root]', root).forEach((container) => {
      if (initialized.has(container)) return;
      const { checkout, consent } = resolveSurface(container);
      if (!consent || !checkout) return;
      initialized.add(container);
      const serverAccepted = consent.dataset.tripsConsentCurrent === 'true';
      const accepted = lifecycle === 'idle'
        ? (clientAccepted === null
            ? serverAccepted
            : Boolean(clientAccepted && clientAcceptedVersion === container.dataset.tripsPolicyVersion))
        : false;
      setCheckoutSurface(container, accepted);
      consent.disabled = lifecycle === 'pending';
      if (lifecycle === 'failed') {
        const status = container.querySelector('[data-trips-consent-status]');
        if (status) status.textContent = RETRY_MESSAGE;
      }
    });
  };

  const install = () => {
    document.addEventListener('change', (event) => {
      if (!event.target.matches('[data-trips-consent]')) return;
      const container = event.target.closest('[data-trips-consent-root]');
      if (container) persistConsent(container, event.target.checked);
    });
    document.addEventListener('shopify:section:load', (event) => initializeConsent(event.target));
    if (MutationObserver) {
      new MutationObserver(() => initializeConsent()).observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }
    initializeConsent();
  };

  return {
    closeAllCheckoutSurfaces,
    initializeConsent,
    install,
    persistConsent,
    setAllCheckoutSurfaces,
  };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  createConsentController({
    document,
    fetch: window.fetch.bind(window),
    routesRoot: window.Shopify?.routes?.root || '/',
    MutationObserver: window.MutationObserver,
  }).install();
}
